import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../Styles/Account.css';
import * as XLSX from 'xlsx';

const Account = () => {
  const location = useLocation();
  const agentId = location.state?.agentId;

  const [walletData, setWalletData] = useState(null);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const res = await fetch(`https://b2bbackend.dxbhabibi.com/api/b2/b2b.rou.w9l0l1/wa78l64ts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agentId }),
        });

        if (!res.ok) throw new Error('Wallet fetch failed');

        const allWallets = await res.json();
        const matchedWallet = allWallets.find(w => w.userId?._id === agentId);
        if (!matchedWallet) {
          console.error('No wallet found for agent');
          return;
        }

        const b2bAuth = JSON.parse(localStorage.getItem('b2bAuthToken'));
        const token = b2bAuth?.token;
        let externalApiToken = b2bAuth?.externalApiToken;

        if (externalApiToken?.startsWith('Bearer ')) {
          externalApiToken = externalApiToken.replace('Bearer ', '');
        }

        const transactions = await Promise.all(
          matchedWallet.transactions.map(async (txn) => {
            let destination = '-';
            let travelDate = '-';
            let bookingId = txn.reference || '-';

            if (txn.reference) {
              try {
                const bookingRes = await fetch(
                  `https://b2bbackend.dxbhabibi.com/api/b2/b2b.rou.g5c6y4/le123dgr6/${txn.reference}`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`,
                      'external-authorization': `Bearer ${externalApiToken}`
                    },
                    body: JSON.stringify({ booking_id: txn.reference }),
                  }
                );

                if (bookingRes.ok) {
                  const response = await bookingRes.json();
                  const bookingData = response?.data;
                  bookingId = bookingData?.booking_id || txn.reference;
                  destination = bookingData?.sector || '-';
                  travelDate = bookingData?.departure_date || '-';
                }
              } catch (err) {
                console.warn(`Failed to fetch booking for ${txn.reference}:`, err.message);
              }
            }

            const formattedDate = txn.date
              ? new Date(txn.date).toLocaleDateString('en-US', {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                })
              : '-';

            return {
              reference: bookingId,
              date: formattedDate,
              particulars: txn.purpose || txn.description || '-',
              destination,
              travelDate,
              amount: txn.type === 'debit' ? `-${txn.amount}` : txn.amount,
              ledgerBalance: '',
            };
          })
        );

        setWalletData({
          walletAmount: matchedWallet.amount,
          user: matchedWallet.userId,
          transactions,
        });
        setFilteredTransactions(transactions);
      } catch (error) {
        console.error('Error fetching wallet/booking data:', error);
      }
    };

    if (agentId) fetchWallet();
  }, [agentId]);

  const exportToExcel = () => {
    const worksheetData = filteredTransactions.map(item => ({
      'Ref No': item.reference,
      'Entry Date': item.date,
      'Particulars': item.particulars,
      'Destination': item.destination,
      'Travel Date': item.travelDate,
      'Amount': item.amount,
      'Ledger Balance': walletData?.walletAmount || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ledger');
    XLSX.writeFile(workbook, 'Account_Ledger.xlsx');
  };

  const handleFilter = () => {
    if (!walletData) return;

    let filtered = walletData.transactions;

    if (fromDate) {
      const parsedFrom = new Date(fromDate);
      filtered = filtered.filter(txn => {
        const txnDateParts = txn.date.split('/');
        const txnDate = new Date(`${txnDateParts[2]}-${txnDateParts[0]}-${txnDateParts[1]}`);
        return txnDate >= parsedFrom;
      });
    }
    if (toDate) {
      const parsedTo = new Date(toDate);
      filtered = filtered.filter(txn => {
        const txnDateParts = txn.date.split('/');
        const txnDate = new Date(`${txnDateParts[2]}-${txnDateParts[0]}-${txnDateParts[1]}`);
        return txnDate <= parsedTo;
      });
    }
    if (searchTerm) {
      filtered = filtered.filter(txn =>
        txn.particulars?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        txn.reference?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  };

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentEntries = filteredTransactions.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredTransactions.length / entriesPerPage);

  return (
    <div>
      <div className="account-button">
        <span>BALANCE AED {walletData?.walletAmount || 0}</span>
        <span> | </span>
        <span>LEDGER AED {walletData?.walletAmount || 0}</span>
        <span> | </span>
        <span>CREDIT DUE AED </span>
      </div>

      <div className="account-entries">
        <div className="entries-header">
          <div>Show Entries 10</div>

          <div className="entries-controls">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button onClick={handleFilter}>Search</button>
            <button onClick={exportToExcel}>Download</button>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#Ref no</th>
              <th>Entry Date</th>
              <th>Particulars</th>
              <th>Destination</th>
              <th>Travel Date</th>
              <th>Amount</th>
              <th>Ledger Balance</th>
            </tr>
          </thead>
          <tbody>
            {currentEntries.map((txn, index) => (
              <tr key={index}>
                <td>{txn.reference}</td>
                <td>{txn.date}</td>
                <td>{txn.particulars}</td>
                <td>{txn.destination}</td>
                <td>{txn.travelDate}</td>
                <td>{txn.amount}</td>
                <td>{walletData?.walletAmount || 0}</td>
              </tr>
            ))}
            {currentEntries.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>No transactions found</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
          <span> Page {currentPage} of {totalPages} </span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default Account;
